import { type MutableRefObject, type Ref } from 'react';

/**
 * Assign a value to a single ref, whether it's a callback ref or an object ref.
 * Call this from a ref callback / effect (never during render) to forward a
 * consumer ref to a node.
 */
export function assignRef<T>(ref: Ref<T> | undefined, value: T | null): void {
  if (!ref) return;
  if (typeof ref === 'function') {
    ref(value);
  } else {
    (ref as MutableRefObject<T | null>).current = value;
  }
}

/**
 * Compose multiple refs into a single ref callback. Each provided ref —
 * callback or object — receives the same instance. Used to attach an extra ref
 * to a node without clobbering a consumer ref that a component is already
 * threading through its `forwardRef`.
 */
export function mergeRefs<T>(...refs: Array<Ref<T> | undefined>): (instance: T | null) => void {
  return (instance: T | null) => {
    for (const ref of refs) {
      assignRef(ref, instance);
    }
  };
}
